/**
 * Letter of the alphabet dropdown (PDP).
 * A disclosure button that reveals a list of links to the other products in the letter group.
 */

class LetterOfTheAlphabet extends HTMLElement {
  #toggle = null;
  #list = null;

  connectedCallback() {
    this.#toggle = this.querySelector('.letter-of-the-alphabet__toggle');
    this.#list = this.querySelector('.letter-of-the-alphabet__list');

    if (!this.#toggle || !this.#list) return;

    this.#toggle.addEventListener('click', this.#onToggleClick);
    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('focusout', this.#onFocusOut);
    // pointerdown + capture: fires even where `click` doesn't reach document (iOS taps on non-interactive
    // elements, Swiper gallery swallowing clicks)
    document.addEventListener('pointerdown', this.#onDocumentPointerDown, true);
  }

  disconnectedCallback() {
    this.#toggle?.removeEventListener('click', this.#onToggleClick);
    this.removeEventListener('keydown', this.#onKeydown);
    this.removeEventListener('focusout', this.#onFocusOut);
    document.removeEventListener('pointerdown', this.#onDocumentPointerDown, true);
  }

  get isOpen() {
    return this.#toggle?.getAttribute('aria-expanded') === 'true';
  }

  get #links() {
    return [...this.#list.querySelectorAll('.letter-of-the-alphabet__link')];
  }

  open() {
    if (this.isOpen) return;

    this.#toggle.setAttribute('aria-expanded', 'true');
    this.#list.hidden = false;
    this.classList.add('letter-of-the-alphabet--open');
    this.#list.querySelector('[aria-current="page"]')?.scrollIntoView({ block: 'nearest' });
  }

  close({ focusToggle = false } = {}) {
    if (!this.isOpen) return;

    this.#toggle.setAttribute('aria-expanded', 'false');
    this.#list.hidden = true;
    this.classList.remove('letter-of-the-alphabet--open');

    if (focusToggle) this.#toggle.focus();
  }

  #onToggleClick = () => {
    this.isOpen ? this.close() : this.open();
  };

  #onDocumentPointerDown = (event) => {
    if (!this.contains(event.target)) this.close();
  };

  /** Keyboard users tabbing out of the dropdown. A null relatedTarget is a click elsewhere — handled above. */
  #onFocusOut = (event) => {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) this.close();
  };

  #onKeydown = (event) => {
    if (event.key === 'Escape') {
      this.close({ focusToggle: true });
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    event.preventDefault();
    this.open();

    const links = this.#links;
    if (!links.length) return;

    const currentIndex = links.indexOf(document.activeElement);

    if (currentIndex === -1) {
      const selected = links.find((link) => link.getAttribute('aria-current') === 'page');
      (selected ?? links[0]).focus();
      return;
    }

    const step = event.key === 'ArrowDown' ? 1 : -1;
    const nextIndex = Math.min(Math.max(currentIndex + step, 0), links.length - 1);
    links[nextIndex].focus();
  };
}

if (!customElements.get('letter-of-the-alphabet')) {
  customElements.define('letter-of-the-alphabet', LetterOfTheAlphabet);
}
